/**
 * /manual — the sign-in hop for the customer user manual.
 *
 * The manual is served by the `userManual` function at /user-manual/ and gated
 * by a session cookie scoped to that path. When the cookie is missing or expired
 * the function redirects here with `?next=<path>` (the browser keeps the
 * #section). ProtectedRoute, plus the app-wide 2FA and approval gates, make sure
 * the user is signed in first; this page then trades the Firebase ID token for
 * the cookie and goes back. See plans/user-manual-behind-login.md.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, Button, Spinner } from '@/components/ui';

const MANUAL_HOME = '/user-manual/';
const LAST_HOP_KEY = 'userManual:lastHop';
// Coming back here this soon after sending the user to the manual means the
// browser didn't keep the cookie; stop instead of bouncing forever.
const LOOP_WINDOW_MS = 15_000;

/** `next` comes from the URL, so only ever return into the manual. */
export function manualTarget(next: string | null): string {
  return next && /^\/user-manual\/[A-Za-z0-9._/-]*$/.test(next) ? next : MANUAL_HOME;
}

function readLastHop(): number {
  try {
    return Number(sessionStorage.getItem(LAST_HOP_KEY) || 0);
  } catch {
    return 0;
  }
}

function writeLastHop(value: number | null): void {
  try {
    if (value === null) sessionStorage.removeItem(LAST_HOP_KEY);
    else sessionStorage.setItem(LAST_HOP_KEY, String(value));
  } catch {
    /* storage blocked: the loop guard just doesn't apply */
  }
}

export default function UserManualRedirect() {
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!currentUser) return;
    if (attempt === 0 && Date.now() - readLastHop() < LOOP_WINDOW_MS) {
      setFailed(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const token = await currentUser.getIdToken();
        const res = await fetch('/user-manual/session', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`session request failed (${res.status})`);
        if (cancelled) return;
        writeLastHop(Date.now());
        const next = manualTarget(new URLSearchParams(location.search).get('next'));
        window.location.replace(`${next}${location.hash}`);
      } catch (err) {
        console.error('[userManual] could not open the user manual', err);
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // location is fixed for the life of this hop page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, attempt]);

  if (failed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="w-full max-w-md space-y-4">
          <div className="flex items-center gap-2 text-foreground">
            <BookOpen className="h-5 w-5" />
            <h1 className="text-lg font-semibold">User manual</h1>
          </div>
          <Alert variant="destructive">
            <AlertDescription>
              We couldn't open the user manual. If your browser blocks cookies for this site, allow
              them and try again.
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button
              onClick={() => {
                writeLastHop(null);
                setFailed(false);
                setAttempt((a) => a + 1);
              }}
            >
              Try again
            </Button>
            <Button variant="outline" onClick={() => navigate('/', { replace: true })}>
              Back to the console
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-background">
      <Spinner size="lg" className="text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Opening the user manual…</p>
    </div>
  );
}
